import { IDENTITY, multiply, type Matrix } from '../geometry/matrix';
import type { Design, GroupNode, ProjectDocument, VectorNode } from './types';

/**
 * Immutable helpers for the design node tree. Updates return new objects along the
 * changed path only, so unchanged subtrees keep their identity (structural sharing),
 * which keeps undo history compact and lets renderers skip unchanged nodes.
 */
export interface NodeLocation {
  readonly node: VectorNode;
  /** Ancestor groups from the root down to the direct parent. */
  readonly ancestors: readonly GroupNode[];
  readonly index: number;
  /** Parent-to-design (world) matrix: product of ancestor transforms. */
  readonly parentWorld: Matrix;
  /** Node-to-design matrix: `parentWorld × node.transform`. */
  readonly world: Matrix;
}

export function findDesign(doc: ProjectDocument, designId: string): Design | undefined {
  return doc.designs.find((d) => d.id === designId);
}

export function locateNode(design: Design, nodeId: string): NodeLocation | null {
  // Search first, then compose matrices along the one path found: the search itself
  // does no arithmetic or copying, which matters with thousands of nodes (docs/11).
  const ancestors: GroupNode[] = [];
  const search = (nodes: readonly VectorNode[]): { node: VectorNode; index: number } | null => {
    for (let index = 0; index < nodes.length; index += 1) {
      const node = nodes[index]!;
      if (node.id === nodeId) return { node, index };
      if (node.type === 'group') {
        ancestors.push(node);
        const found = search(node.children);
        if (found) return found;
        ancestors.pop();
      }
    }
    return null;
  };
  const found = search(design.nodes);
  if (!found) return null;
  let parentWorld: Matrix = IDENTITY;
  for (const group of ancestors) parentWorld = multiply(parentWorld, group.transform);
  return {
    node: found.node,
    ancestors,
    index: found.index,
    parentWorld,
    world: multiply(parentWorld, found.node.transform),
  };
}

export function* walkNodes(
  nodes: readonly VectorNode[],
  parentWorld: Matrix = IDENTITY,
  depth = 1,
): Generator<{ node: VectorNode; world: Matrix; depth: number }> {
  for (const node of nodes) {
    const world = multiply(parentWorld, node.transform);
    yield { node, world, depth };
    if (node.type === 'group') yield* walkNodes(node.children, world, depth + 1);
  }
}

export function collectNodeIds(node: VectorNode, into: Set<string> = new Set()): Set<string> {
  into.add(node.id);
  if (node.type === 'group') for (const child of node.children) collectNodeIds(child, into);
  return into;
}

export function collectDesignNodeIds(design: Design): Set<string> {
  const ids = new Set<string>();
  for (const node of design.nodes) collectNodeIds(node, ids);
  return ids;
}

/** Replaces one node (found anywhere in the tree) using `update`; returns the same design if absent. */
export function updateNode(
  design: Design,
  nodeId: string,
  update: (node: VectorNode) => VectorNode,
): Design {
  let changed = false;
  const visit = (nodes: readonly VectorNode[]): readonly VectorNode[] => {
    let result: VectorNode[] | null = null;
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i]!;
      let next: VectorNode = node;
      if (node.id === nodeId) {
        next = update(node);
        changed = true;
      } else if (node.type === 'group') {
        const children = visit(node.children);
        if (children !== node.children) next = { ...node, children };
      }
      if (next !== node) {
        result ??= nodes.slice();
        result[i] = next;
      }
    }
    return result ?? nodes;
  };
  const nodes = visit(design.nodes);
  return changed ? { ...design, nodes } : design;
}

/** Removes every node whose ID is in `ids` (descendants go with their group). */
export function removeNodes(design: Design, ids: ReadonlySet<string>): Design {
  const visit = (nodes: readonly VectorNode[]): readonly VectorNode[] => {
    let changed = false;
    const out: VectorNode[] = [];
    for (const node of nodes) {
      if (ids.has(node.id)) {
        changed = true;
        continue;
      }
      if (node.type === 'group') {
        const children = visit(node.children);
        if (children !== node.children) {
          changed = true;
          out.push({ ...node, children });
          continue;
        }
      }
      out.push(node);
    }
    return changed ? out : nodes;
  };
  const nodes = visit(design.nodes);
  return nodes === design.nodes ? design : { ...design, nodes };
}

export function replaceDesign(doc: ProjectDocument, design: Design): ProjectDocument {
  const index = doc.designs.findIndex((d) => d.id === design.id);
  if (index < 0 || doc.designs[index] === design) return doc;
  const designs = doc.designs.slice();
  designs[index] = design;
  return { ...doc, designs };
}

/** Maps every node in a subtree, children first, preserving identity when unchanged. */
export function mapSubtree(node: VectorNode, fn: (node: VectorNode) => VectorNode): VectorNode {
  if (node.type === 'group') {
    let changed = false;
    const children = node.children.map((child) => {
      const next = mapSubtree(child, fn);
      if (next !== child) changed = true;
      return next;
    });
    return fn(changed ? { ...node, children } : node);
  }
  return fn(node);
}

/** Returns true if `ancestorId` is `nodeId` itself or one of its ancestors. */
export function isSelfOrAncestor(design: Design, ancestorId: string, nodeId: string): boolean {
  const location = locateNode(design, nodeId);
  if (!location) return false;
  return location.node.id === ancestorId || location.ancestors.some((a) => a.id === ancestorId);
}
